/**
 * <lp-carousel> — Pfeil-Navigation für die scroll-snap-Karussells der Startseite.
 *
 * Das Scrollen selbst macht CSS (overflow-x + scroll-snap). Diese Klasse fügt
 * nur die Pfeile hinzu: ohne JS bleibt das Karussell per Wischen/Trackpad voll
 * bedienbar, die Pfeile werden dann gar nicht erst eingeblendet (sie stehen im
 * Markup auf [hidden] und werden hier freigeschaltet).
 *
 * Erwartetes Markup:
 *   <lp-carousel>
 *     <ul data-track> <li>…</li> </ul>
 *     <div hidden data-nav>
 *       <button data-dir="prev">…</button>
 *       <button data-dir="next">…</button>
 *     </div>
 *   </lp-carousel>
 */
class LpCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.nav = this.querySelector('[data-nav]');
    if (!this.track || !this.nav) return;

    this.prev = this.nav.querySelector('[data-dir="prev"]');
    this.next = this.nav.querySelector('[data-dir="next"]');

    // Bei nur einer sichtbaren Karte gibt es nichts zu blättern. Die Toleranz
    // von 4px fängt Subpixel-Rundungen ab (fluide clamp()-Breiten ergeben
    // selten glatte Ganzzahlen).
    if (this.track.scrollWidth <= this.track.clientWidth + 4) return;

    this.nav.hidden = false;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.syncButtons();

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.syncButtons());
      this.observer.observe(this.track);
    }

    this.syncButtons();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  /**
   * Blättert um genau eine Karte (inkl. Gap) weiter. Karten sind gleich breit,
   * deshalb reicht der Abstand der ersten beiden Slide-Kanten als Schrittweite.
   */
  scrollByPage(direction) {
    const slides = this.track.querySelectorAll('[data-slide]');
    let step = this.track.clientWidth * 0.8;

    if (slides.length > 1) {
      step = slides[1].offsetLeft - slides[0].offsetLeft;
    } else if (slides.length === 1) {
      step = slides[0].offsetWidth;
    }

    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  syncButtons() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    // 2px Toleranz: subpixel-Scrollpositionen erreichen die Grenzen sonst nie
    // exakt und der Pfeil bliebe dauerhaft aktiv.
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max - 2;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
  }
}

if (!customElements.get('lp-carousel')) {
  customElements.define('lp-carousel', LpCarousel);
}
